import { NO, YES } from "uicore-ts"
import type {
    RHOffererDashboardProcurement,
    RHProcurementExposureReason,
    RHProcurementInformativeStatus,
    RHPublishedProcurementRow
} from "../SocketClientFunctions"


export type RHCompanyScopedPublishedProcurementRow = Omit<
    RHPublishedProcurementRow,
    "reconciliationRounds"
> & {
    exposureReasons: RHProcurementExposureReason[]
    reconciliationRounds?: RHCompanyScopedPublishedProcurementRow[]
}


/**
 * Replaces the all-affiliations annotations of the public procurement feed
 * with the authoritative state returned for the active offerer company.
 */
export class OffererCompanyProcurementScope {

    static publishedRowsForOffererCompany(
        publishedRows: RHPublishedProcurementRow[],
        offererCompanyRows: RHOffererDashboardProcurement[]
    ): RHCompanyScopedPublishedProcurementRow[] {
        const offererCompanyRowByProcurementId = new Map<string, RHOffererDashboardProcurement>(
            offererCompanyRows.map((row): [string, RHOffererDashboardProcurement] => [
                row._id?.toString() ?? "",
                row
            ])
        )
        return OffererCompanyProcurementScope._publishedRowsForOffererCompany(
            publishedRows,
            offererCompanyRowByProcurementId
        )
    }


    static _publishedRowsForOffererCompany(
        publishedRows: RHPublishedProcurementRow[],
        offererCompanyRowByProcurementId: Map<string, RHOffererDashboardProcurement>
    ): RHCompanyScopedPublishedProcurementRow[] {
        return publishedRows
            .filter(row => {
                if (row.publicationVisibility !== "directed") {
                    return YES
                }
                return offererCompanyRowByProcurementId.has(row._id?.toString() ?? "")
            })
            .map(row => OffererCompanyProcurementScope._publishedRowForOffererCompany(
                row,
                offererCompanyRowByProcurementId
            ))
    }


    static _publishedRowForOffererCompany(
        publishedRow: RHPublishedProcurementRow,
        offererCompanyRowByProcurementId: Map<string, RHOffererDashboardProcurement>
    ): RHCompanyScopedPublishedProcurementRow {
        const procurementId = publishedRow._id?.toString() ?? ""
        const offererCompanyRow = offererCompanyRowByProcurementId.get(procurementId)
        let informativeStatus: RHProcurementInformativeStatus | undefined
        if (offererCompanyRow) {
            informativeStatus = offererCompanyRow.offererInformativeStatus
        }
        else if (publishedRow.offererInformativeStatus?.kind === "deadlineCountdown") {
            informativeStatus = publishedRow.offererInformativeStatus
        }

        return {
            ...publishedRow,
            isCurrentUserCompanyRegisteredAsTenderer:
                offererCompanyRow?.isCurrentUserCompanyRegisteredAsTenderer ?? NO,
            requiresOffererAttention: offererCompanyRow?.requiresOffererAttention ?? NO,
            hasSubmittedOfferForCurrentCompany:
                offererCompanyRow?.hasSubmittedOfferForCurrentCompany ?? NO,
            hasViewableOfferDraftForCurrentCompany:
                offererCompanyRow?.hasViewableOfferDraftForCurrentCompany ?? NO,
            offererSubmissionOutcome: offererCompanyRow?.offererSubmissionOutcome,
            lifecycleState: offererCompanyRow?.lifecycleState ?? publishedRow.lifecycleState,
            offererInformativeStatus: informativeStatus,
            exposureReasons: offererCompanyRow?.exposureReasons ?? [],
            reconciliationRounds: OffererCompanyProcurementScope._publishedRowsForOffererCompany(
                publishedRow.reconciliationRounds ?? [],
                offererCompanyRowByProcurementId
            )
        }
    }

}
